<script setup>
// Campo de formulário reutilizável dos painéis de config de nó.
// Tipos: text|number|textarea|select|checkbox|date|time|datetime-local|range|buttongroup.
// Suporta variable-picker (textarea/text), hint, options.
import { computed } from 'vue';
import VariablePicker from './VariablePicker.vue';
import KanbanSelect from '../KanbanSelect.vue';

const props = defineProps({
  modelValue: { type: [String, Number, Boolean], default: '' },
  label: { type: String, default: '' },
  type: { type: String, default: 'text' },
  placeholder: { type: String, default: '' },
  options: { type: Array, default: () => [] },
  rows: { type: Number, default: 3 },
  hint: { type: String, default: '' },
  min: { type: [Number, String], default: undefined },
  max: { type: [Number, String], default: undefined },
  step: { type: [Number, String], default: undefined },
  picker: { type: Boolean, default: false },
});
const emit = defineEmits(['update:modelValue']);

const val = computed({
  get: () => props.modelValue,
  set: v => emit('update:modelValue', v),
});
const insert = token => {
  val.value = `${props.modelValue || ''}${token}`;
};
</script>

<template>
  <div class="mb-3">
    <div
      v-if="label && type !== 'checkbox'"
      class="flex items-center justify-between mb-0.5"
    >
      <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
        label
      }}</label>
      <VariablePicker v-if="picker" @select="insert" />
    </div>

    <textarea
      v-if="type === 'textarea'"
      v-model="val"
      :rows="rows"
      class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
      :placeholder="placeholder"
    />

    <KanbanSelect
      v-else-if="type === 'select'"
      v-model="val"
      :options="options"
      :placeholder="placeholder"
    />

    <label
      v-else-if="type === 'checkbox'"
      class="flex items-center gap-2 text-sm text-n-slate-12"
    >
      <input v-model="val" type="checkbox" /> {{ label }}
    </label>

    <div v-else-if="type === 'buttongroup'" class="flex gap-1">
      <button
        v-for="o in options"
        :key="o.value"
        type="button"
        class="flex-1 px-2 py-1.5 text-xs rounded-md border border-n-weak text-n-slate-11 bg-n-background"
        :class="{ 'bg-n-brand text-white border-n-brand': val === o.value }"
        @click="val = o.value"
      >
        {{ o.label }}
      </button>
    </div>

    <div v-else-if="type === 'range'" class="flex items-center gap-2">
      <input
        v-model.number="val"
        type="range"
        :min="min"
        :max="max"
        :step="step"
        class="flex-1"
      />
      <span class="w-10 text-xs text-right text-n-slate-11">{{ val }}</span>
    </div>

    <input
      v-else
      v-model="val"
      :type="type"
      class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
      :placeholder="placeholder"
      :min="min"
      :max="max"
      :step="step"
    />

    <p v-if="hint" class="text-xs text-n-slate-10 mt-1" v-html="hint" />
  </div>
</template>
